<template>
    <div class="permissions-layout">
        <!-- Sidebar -->
        <aside class="permissions-sidebar">
            <div class="sidebar-header">
                <i class="fal fa-layer-group sidebar-header-icon"></i>
                <span>{{ $t('permissions.modules') }}</span>
            </div>
            <nav class="sidebar-nav">
                <button
                    v-for="group in permissionGroups"
                    :key="group.key"
                    class="sidebar-item"
                    :class="{ 'sidebar-item--active': selectedGroup === group.key }"
                    @click="selectedGroup = group.key"
                >
                    <i :class="group.icon" class="sidebar-item-icon"></i>
                    <span class="sidebar-item-label">{{ $t('permissions.groups.' + group.key) }}</span>
                </button>
            </nav>
        </aside>

        <!-- Main content -->
        <div class="permissions-main">
            <template v-if="activeGroup">
                <!-- Group header -->
                <div class="group-header">
                    <div class="group-header-info">
                        <div class="group-header-icon">
                            <i :class="activeGroup.icon"></i>
                        </div>
                        <div>
                            <h3 class="group-title">{{ $t('permissions.groups.' + activeGroup.key) }}</h3>
                            <p class="group-subtitle">
                                {{ activeGroup.permissions.length }} {{ $t('permissions.permission_count') }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Roles header row -->
                <div class="matrix-container">
                    <table class="matrix-table">
                        <thead>
                            <tr>
                                <th class="matrix-th-label">
                                    {{ $t('permissions.table.permission') }}
                                </th>
                                <th
                                    v-for="role in roles"
                                    :key="'th-' + role.id"
                                    class="matrix-th-role"
                                >
                                    <div class="role-badge">
                                        <i class="fal fa-user-tag role-badge-icon"></i>
                                        {{ role.name }}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- Select all row -->
                            <tr class="matrix-row matrix-row--select-all">
                                <td class="matrix-cell-label">
                                    <span class="select-all-label">
                                        {{ $t('permissions.table.select_all') }}
                                    </span>
                                </td>
                                <td
                                    v-for="role in roles"
                                    :key="'selectall-' + role.id"
                                    class="matrix-cell-toggle"
                                >
                                    <label class="toggle">
                                        <input
                                            type="checkbox"
                                            :checked="isGroupFullyChecked(role.id, activeGroup)"
                                            :disabled="isSaving"
                                            @change="toggleAllForGroup(role.id, activeGroup)"
                                        />
                                        <span class="toggle-slider"></span>
                                    </label>
                                </td>
                            </tr>
                            <!-- Permission rows -->
                            <tr
                                v-for="perm in activeGroup.permissions"
                                :key="perm"
                                class="matrix-row"
                                :class="{ 'matrix-row--modified': isPermissionModified(perm) }"
                            >
                                <td class="matrix-cell-label">
                                    {{ getPermissionLabel(perm) }}
                                </td>
                                <td
                                    v-for="role in roles"
                                    :key="role.id + '-' + perm"
                                    class="matrix-cell-toggle"
                                >
                                    <label class="toggle">
                                        <input
                                            type="checkbox"
                                            :checked="hasPermission(role.id, perm)"
                                            :disabled="isSaving"
                                            @change="togglePermission(role.id, perm, $event)"
                                        />
                                        <span class="toggle-slider"></span>
                                    </label>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>

            <div v-else class="empty-state">
                <i class="fal fa-hand-pointer empty-state-icon"></i>
                <p>{{ $t('permissions.select_module') }}</p>
            </div>

            <!-- Footer actions -->
            <div class="permissions-footer">
                <div class="changes-indicator" v-if="changesCount > 0">
                    <i class="fal fa-pen-to-square"></i>
                    {{ changesCount }} {{ $t('permissions.modifications') }}
                </div>
                <div v-else></div>
                <button
                    class="btn-save"
                    :disabled="!hasChanges || isSaving"
                    @click="saveAll"
                >
                    <i v-if="isSaving" class="fas fa-spinner fa-spin btn-save-icon"></i>
                    <i v-else class="fas fa-save btn-save-icon"></i>
                    {{ $t('permissions.table.save') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
const MODULE_ORDER = ['posts', 'comments', 'messages', 'forms', 'form-types', 'response-templates', 'news', 'news-categories', 'users', 'company']

const MODULE_ICONS = {
    posts: 'fal fa-ticket',
    comments: 'fal fa-comments',
    messages: 'fal fa-envelope',
    forms: 'fal fa-poll',
    'form-types': 'fal fa-layer-group',
    'response-templates': 'fal fa-reply',
    news: 'fal fa-newspaper',
    'news-categories': 'fal fa-tags',
    users: 'fal fa-users',
    company: 'fal fa-building'
}

export default {
    name: 'PermissionsTable',
    props: {
        roles: {
            type: Array,
            required: true
        },
        defaults: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            localPermissions: {},
            originalPermissions: {},
            modified: {},
            isSaving: false,
            selectedGroup: null
        }
    },
    computed: {
        allPermissions() {
            const set = new Set()
            this.defaults.forEach((role) => {
                (role.permissions || []).forEach((p) => set.add(p))
            })
            return Array.from(set)
        },
        hasChanges() {
            return Object.values(this.modified).some((v) => v)
        },
        changesCount() {
            return Object.values(this.modified).filter((v) => v).length
        },
        permissionGroups() {
            const grouped = {}
            this.allPermissions.forEach((perm) => {
                const module = perm.split('.')[0]
                if (!grouped[module]) {
                    grouped[module] = []
                }
                grouped[module].push(perm)
            })
            return MODULE_ORDER
                .filter((key) => grouped[key])
                .map((key) => ({
                    key,
                    icon: MODULE_ICONS[key] || 'fal fa-cog',
                    permissions: grouped[key]
                }))
        },
        activeGroup() {
            return this.permissionGroups.find((g) => g.key === this.selectedGroup) || null
        }
    },
    watch: {
        roles: {
            handler() {
                this.initLocalPermissions()
            },
            deep: true,
            immediate: true
        },
        permissionGroups: {
            handler(groups) {
                if (!this.selectedGroup && groups.length > 0) {
                    this.selectedGroup = groups[0].key
                }
            },
            immediate: true
        }
    },
    methods: {
        initLocalPermissions() {
            const local = {}
            const original = {}
            this.roles.forEach((role) => {
                local[role.id] = [...(role.permissions || [])]
                original[role.id] = [...(role.permissions || [])]
            })
            this.localPermissions = local
            this.originalPermissions = original
        },
        getPermissionLabel(perm) {
            const list = this.$t('permissions.list')
            if (list && list[perm]) return list[perm]
            return perm
        },
        hasPermission(roleId, perm) {
            const perms = this.localPermissions[roleId] || []
            return perms.includes(perm)
        },
        isGroupFullyChecked(roleId, group) {
            const perms = this.localPermissions[roleId] || []
            return group.permissions.every((p) => perms.includes(p))
        },
        isPermissionModified(perm) {
            return this.roles.some((role) => {
                const original = this.originalPermissions[role.id] || []
                const current = this.localPermissions[role.id] || []
                const wasActive = original.includes(perm)
                const isActive = current.includes(perm)
                return wasActive !== isActive
            })
        },
        getGroupModifiedCount(groupKey) {
            const group = this.permissionGroups.find((g) => g.key === groupKey)
            if (!group) return 0
            return group.permissions.filter((perm) => this.isPermissionModified(perm)).length
        },
        toggleAllForGroup(roleId, group) {
            if (!this.localPermissions[roleId]) {
                this.$set(this.localPermissions, roleId, [])
            }
            const perms = this.localPermissions[roleId]
            const allChecked = this.isGroupFullyChecked(roleId, group)
            group.permissions.forEach((p) => {
                const index = perms.indexOf(p)
                if (allChecked && index > -1) {
                    perms.splice(index, 1)
                } else if (!allChecked && index === -1) {
                    perms.push(p)
                }
            })
            this.$set(this.modified, roleId, true)
        },
        togglePermission(roleId, perm, event) {
            if (!this.localPermissions[roleId]) {
                this.$set(this.localPermissions, roleId, [])
            }
            const perms = this.localPermissions[roleId]
            const index = perms.indexOf(perm)
            if (event.target.checked && index === -1) {
                perms.push(perm)
            } else if (!event.target.checked && index > -1) {
                perms.splice(index, 1)
            }
            this.$set(this.modified, roleId, true)
        },
        saveAll() {
            const roles = {}
            this.roles.forEach((role) => {
                if (!this.modified[role.id]) return
                const original = this.originalPermissions[role.id] || []
                const current = this.localPermissions[role.id] || []
                const diff = {}
                // Permissions added (now true)
                current.forEach((perm) => {
                    if (!original.includes(perm)) diff[perm] = true
                })
                // Permissions removed (now false)
                original.forEach((perm) => {
                    if (!current.includes(perm)) diff[perm] = false
                })
                if (Object.keys(diff).length > 0) {
                    roles[role.id] = diff
                }
            })
            if (Object.keys(roles).length === 0) return
            this.isSaving = true
            this.$emit('save', {
                roles,
                callback: () => {
                    this.isSaving = false
                    this.modified = {}
                    this.roles.forEach((role) => {
                        this.originalPermissions[role.id] = [...(this.localPermissions[role.id] || [])]
                    })
                }
            })
        }
    }
}
</script>

<style scoped>
/* ── Layout ── */
.permissions-layout {
    display: flex;
    gap: 0;
    min-height: 500px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
}

/* ── Sidebar ── */
.permissions-sidebar {
    width: 240px;
    min-width: 240px;
    background: #f8fafc;
    border-right: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
}

.sidebar-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 20px 20px 16px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.sidebar-header-icon {
    font-size: 0.9rem;
    color: #94a3b8;
}

.sidebar-nav {
    display: flex;
    flex-direction: column;
    padding: 0 8px 16px;
    gap: 2px;
}

.sidebar-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: none;
    background: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
    font-size: 0.9rem;
    color: #475569;
    text-align: left;
    width: 100%;
}

.sidebar-item:hover {
    background: #e2e8f0;
    color: #0f172a;
}

.sidebar-item--active {
    background: #0891b2;
    color: #fff;
    font-weight: 500;
}

.sidebar-item--active:hover {
    background: #0e7490;
    color: #fff;
}

.sidebar-item--active .sidebar-item-icon {
    color: #fff;
}

.sidebar-item-icon {
    width: 18px;
    text-align: center;
    font-size: 0.95rem;
    color: #94a3b8;
    transition: color 0.15s ease;
}

.sidebar-item-label {
    flex: 1;
}

.sidebar-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: #fbbf24;
    color: #78350f;
    font-size: 0.7rem;
    font-weight: 700;
}

.sidebar-item--active .sidebar-badge {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
}

/* ── Main content ── */
.permissions-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

/* ── Group header ── */
.group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
}

.group-header-info {
    display: flex;
    align-items: center;
    gap: 14px;
}

.group-header-icon {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #ecfeff;
    color: #0891b2;
    font-size: 1.15rem;
}

.group-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.group-subtitle {
    font-size: 0.8rem;
    color: #94a3b8;
    margin: 4px 0 0;
}

/* ── Matrix table ── */
.matrix-container {
    flex: 1;
    overflow-x: auto;
    padding: 0;
}

.matrix-table {
    width: 100%;
    border-collapse: collapse;
}

.matrix-th-label {
    text-align: left;
    padding: 12px 24px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    min-width: 220px;
}

.matrix-th-role {
    text-align: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    min-width: 120px;
}

.role-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    background: #0891b2;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 500;
}

.role-badge-icon {
    font-size: 0.75rem;
    color: #fff;
}

/* ── Rows ── */
.matrix-row {
    transition: background 0.1s ease;
}

.matrix-row:hover {
    background: #f8fafc;
}

.matrix-row--select-all {
    background: #f1f5f9;
}

.matrix-row--select-all:hover {
    background: #e8edf2;
}

.matrix-row--modified {
    background: #fefce8;
}

.matrix-row--modified:hover {
    background: #fef9c3;
}

.matrix-cell-label {
    padding: 10px 24px;
    font-size: 0.875rem;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
}

.select-all-label {
    font-weight: 600;
    font-size: 0.8rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.matrix-cell-toggle {
    text-align: center;
    padding: 10px 16px;
    border-bottom: 1px solid #f1f5f9;
}

/* ── Toggle switch ── */
.toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
}

.toggle input {
    display: none;
}

.toggle-slider {
    position: relative;
    width: 36px;
    height: 20px;
    background: #cbd5e1;
    border-radius: 20px;
    transition: all 0.2s ease;
}

.toggle-slider::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.toggle input:checked + .toggle-slider {
    background: #0891b2;
}

.toggle input:checked + .toggle-slider::after {
    transform: translateX(16px);
}

.toggle input:disabled + .toggle-slider {
    opacity: 0.5;
    cursor: not-allowed;
}

.toggle:hover .toggle-slider {
    background: #94a3b8;
}

.toggle:hover input:checked + .toggle-slider {
    background: #0e7490;
}

/* ── Empty state ── */
.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    gap: 12px;
}

.empty-state-icon {
    font-size: 2.5rem;
}

/* ── Footer ── */
.permissions-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
    margin-top: auto;
}

.changes-indicator {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 20px;
    background: #fef3c7;
    color: #92400e;
    font-size: 0.8rem;
    font-weight: 500;
}

.btn-save {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border: none;
    border-radius: 8px;
    background: #0891b2;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
}

.btn-save:hover:not(:disabled) {
    background: #0e7490;
    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.3);
}

.btn-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-save-icon {
    font-size: 0.85rem;
    color: #fff;
}

/* ── Responsive ── */
@media (max-width: 768px) {
    .permissions-layout {
        flex-direction: column;
        min-height: auto;
    }

    .permissions-sidebar {
        width: 100%;
        min-width: 100%;
        border-right: none;
        border-bottom: 1px solid #e2e8f0;
    }

    .sidebar-nav {
        flex-direction: row;
        overflow-x: auto;
        padding: 0 8px 12px;
        gap: 4px;
        -webkit-overflow-scrolling: touch;
    }

    .sidebar-item {
        white-space: nowrap;
        padding: 8px 14px;
        min-width: fit-content;
    }

    .sidebar-badge {
        min-width: 18px;
        height: 18px;
        font-size: 0.65rem;
    }

    .group-header {
        padding: 16px;
    }

    .matrix-cell-label {
        padding: 10px 16px;
    }

    .matrix-th-label {
        padding: 12px 16px;
        min-width: 160px;
    }

    .matrix-th-role {
        padding: 12px 10px;
        min-width: 100px;
    }

    .permissions-footer {
        padding: 12px 16px;
    }
}
</style>
